import { DatePipe } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiService } from '@services/api.service';
import { LIMITE_ARQUIVOS_MB } from '../../../../shared/interceptors/file-size.interceptor';
import { SEM_LOADING } from '../../../../shared/interceptors/loading.interceptor';
import { SituacaoVistoria, Vistoria } from '../models/vistoria';
import { VistoriaListItem } from '../../listar-vistorias/models/vistoria-list-item';

export interface VerificacaoFotosVistoria {
  total: number;
  fotosComErro: string[];
}

export function vistoriaVazia(): Vistoria {
  return {
    id: 0,
    modeloVistoriaId: null,
    modeloVistoriaNome: null,
    endereco: '',
    vistoriador: '',
    data: null,
    situacao: SituacaoVistoria.EmAndamento,
    dataModificacao: null,
    templateHtml: '',
    campos: [],
    comodos: [],
  };
}

@Injectable({
  providedIn: 'root'
})
export class VistoriaApiService extends ApiService {

  private readonly baseRoute = '/vistoria';

  constructor(
    public override http: HttpClient,
    private datePipe: DatePipe,
  ) {
    super(http);
  }

  public listar(): Observable<VistoriaListItem[]> {
    return this.get<VistoriaListItem[]>(this.baseRoute);
  }

  public getById(id: number): Observable<Vistoria> {
    return this.get<any>(`${this.baseRoute}/${id}`).pipe(map(raw => this.mapParaVistoria(raw)));
  }

  public getTemplateHtml(id: number): Observable<{ html: string }> {
    return this.get<{ html: string }>(`${this.baseRoute}/${id}/template-html`);
  }

  public getTemplateHtmlPadrao(): Observable<{ html: string }> {
    return this.get<{ html: string }>(`${this.baseRoute}/template-html-padrao`);
  }

  /** Cria a vistoria com as fotos pendentes (multipart) e devolve a vistoria salva, igual ao getById. */
  public criar(vistoria: Vistoria): Observable<Vistoria> {
    return this.http.post<any>(`${this.baseUrl}${this.baseRoute}`, this.montarMultipart(vistoria), { context: this.contextoUpload() })
      .pipe(map(raw => this.mapParaVistoria(raw)));
  }

  /** Edita a vistoria enviando só as fotos ainda pendentes; devolve a vistoria salva, igual ao getById. */
  public editar(id: number, vistoria: Vistoria): Observable<Vistoria> {
    return this.http.put<any>(`${this.baseUrl}${this.baseRoute}/${id}`, this.montarMultipart(vistoria), { context: this.contextoUpload() })
      .pipe(map(raw => this.mapParaVistoria(raw)));
  }

  /** Foto (ou miniatura) salva, como Blob — o `<img src>` não manda o header Authorization. Em segundo
   * plano, sem o overlay de carregamento. */
  public getFoto(vistoriaId: number, chave: string, miniatura: boolean): Observable<Blob> {
    const rota = `${this.baseUrl}${this.baseRoute}/${vistoriaId}/fotos/${chave}${miniatura ? '/miniatura' : ''}`;
    return this.http.get(rota, { responseType: 'blob', context: new HttpContext().set(SEM_LOADING, true) });
  }

  public renderizarTemplateHtml(vistoria: Vistoria): Observable<{ html: string }> {
    return this.post<{ html: string }>(`${this.baseRoute}/template-html/renderizar`, undefined, this.mapParaRequest(vistoria));
  }

  public gerarPdfPreview(vistoria: Vistoria): Observable<Blob> {
    return this.http.post(`${this.baseUrl}${this.baseRoute}/template-html/pdf`, this.mapParaRequest(vistoria), { responseType: 'blob' });
  }

  /** Laudo em PDF com os dados salvos da vistoria (não é o preview). */
  public gerarPdf(id: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}${this.baseRoute}/${id}/pdf`, { responseType: 'blob' });
  }

  /** Fotos sem arquivo no servidor (ficariam de fora do ZIP), pelo nome que teriam no ZIP. */
  public verificarFotos(id: number): Observable<VerificacaoFotosVistoria> {
    return this.get<VerificacaoFotosVistoria>(`${this.baseRoute}/${id}/fotos/verificar`);
  }

  /** ZIP com as fotos salvas da vistoria, uma pasta por cômodo. */
  public baixarFotos(id: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}${this.baseRoute}/${id}/fotos`, { responseType: 'blob' });
  }

  public concluir(id: number): Observable<void> {
    return this.put<void>(`${this.baseRoute}/${id}/concluir`);
  }

  public deletar(id: number): Observable<void> {
    return this.delete<void>(`${this.baseRoute}/${id}`);
  }

  /** Grafo da vistoria pra API: data como `yyyy-MM-dd` (evita deslocar o dia por fuso) e as fotos só com
   * chave e data — o arquivo vai à parte, no multipart. `agoraAparelho` corrige o relógio do aparelho. */
  private mapParaRequest(vistoria: Vistoria) {
    return {
      ...vistoria,
      data: this.datePipe.transform(vistoria.data, 'yyyy-MM-dd'),
      agoraAparelho: new Date().toISOString(),
      comodos: vistoria.comodos.map(c => ({
        ...c,
        itens: c.itens.map(i => ({
          ...i,
          atributos: i.atributos.map(a => ({
            ...a,
            // Atributo trocado pra texto leva as fotos junto (o servidor as remove).
            fotos: a.ehFoto ? a.fotos.map(f => ({ chave: f.chave, dataCaptura: f.dataCaptura.toISOString() })) : [],
          })),
        })),
      })),
    };
  }

  /** Multipart do salvar: "dados" com o grafo em JSON e um arquivo "fotos" por foto pendente, com a chave como nome. */
  private montarMultipart(vistoria: Vistoria): FormData {
    const formData = new FormData();
    formData.append('dados', JSON.stringify(this.mapParaRequest(vistoria)));

    for (const comodo of vistoria.comodos)
      for (const item of comodo.itens)
        for (const atributo of item.atributos)
          for (const foto of atributo.fotos)
            if (atributo.ehFoto && foto.arquivo) formData.append('fotos', foto.arquivo, foto.chave);

    return formData;
  }

  /** Mesmo limite do servidor pro salvar da vistoria (o padrão do FileSizeInterceptor é 1 MB). */
  private contextoUpload(): HttpContext {
    return new HttpContext().set(LIMITE_ARQUIVOS_MB, 100);
  }

  private mapParaVistoria(raw: any): Vistoria {
    return {
      ...raw,
      data: raw.data ? new Date(raw.data) : null,
      templateHtml: '',
      comodos: raw.comodos.map((c: any) => ({
        ...c,
        itensModelo: [],
        itens: c.itens.map((i: any) => ({
          ...i,
          nomeModelo: null,
          atributos: i.atributos.map((a: any) => ({
            ...a,
            nomeModelo: null,
            fotos: a.fotos.map((f: any) => ({ chave: f.chave, dataCaptura: new Date(f.dataCaptura) })),
          })),
        })),
      })),
    };
  }
}
